import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader2, Plus, X, Baby, Check, Coins, Gift, BookOpen, Flame, Trophy } from "lucide-react";

export default function Family() {
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [activeChild, setActiveChild] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/family/overview");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addChild = async () => {
    if (name.trim().length < 2) return toast.error("Enter a name.");
    await api.post("/family/children", { name, age: age ? Number(age) : null });
    setName(""); setAge(""); setAddOpen(false); load();
    toast.success("Child profile created.");
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="space-y-8" data-testid="family-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sunrise">Parent Dashboard</p>
          <h1 className="mt-2 font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">{data.household?.name}</h1>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-child-btn"
          className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-3 text-sm font-bold tracking-wide text-[#2D3748] hover:scale-105">
          <Plus className="h-4 w-4" /> Add Child
        </button>
      </div>

      {data.children.length === 0 && (
        <div className="rounded-3xl border border-[#FFE8E3] bg-white p-12 text-center text-[#718096]">
          <Baby className="mx-auto h-10 w-10 text-[#FF7E67]" />
          <p className="mt-4">Create a child profile to manage chores, allowance, reading and habits.</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.children.map((c) => (
          <button key={c.id} onClick={() => setActiveChild(c.id)} data-testid={`child-${c.id}`}
            className="rounded-2xl border border-[#FFE8E3] bg-white p-6 text-left hover:-translate-y-1 hover:border-[#FF7E67] transition-all">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-sunrise text-lg font-bold text-[#2D3748]">{c.name.charAt(0)}</div>
              <div><p className="font-nunito text-lg font-bold text-[#2D3748]">{c.name}</p>{c.age && <p className="text-xs text-[#718096]">Age {c.age}</p>}</div>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-[#FF7E67]"><Coins className="h-4 w-4" />{c.balance || 0} pts</span>
              {c.chores_pending > 0 && <span className="rounded-full bg-[#FFE8E3] px-2 py-1 text-xs text-[#FF7E67]">{c.chores_pending} to approve</span>}
            </div>
          </button>
        ))}
      </div>

      {addOpen && (
        <Modal title="Add Child" onClose={() => setAddOpen(false)}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Child's name" data-testid="child-name-input"
            className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          <input value={age} onChange={(e) => setAge(e.target.value)} placeholder="Age (optional)" type="number"
            className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          <button onClick={addChild} data-testid="save-child-btn" className="w-full rounded-full bg-sunrise py-3 font-bold text-[#2D3748]">Create Profile</button>
        </Modal>
      )}

      {activeChild && <ChildModal cid={activeChild} onClose={() => { setActiveChild(null); load(); }} />}
      <p className="text-xs text-[#718096]">Child data is private, parent-controlled, and never used for advertising or profiling.</p>
    </div>
  );
}

function ChildModal({ cid, onClose }) {
  const [d, setD] = useState(null);
  const [inputs, setInputs] = useState({ chore: "", chorePts: 10, allowance: "", reward: "", rewardCost: 20, reading: "", habit: "" });

  const load = useCallback(async () => { const { data } = await api.get(`/family/children/${cid}`); setD(data); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const si = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
  const act = async (fn) => { try { await fn(); await load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (!d) return <Modal title="Loading" onClose={onClose}><Loader2 className="mx-auto h-6 w-6 animate-spin text-[#FF7E67]" /></Modal>;
  const c = d.child;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-[#FFE8E3] bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="child-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-sunrise text-lg font-bold text-[#2D3748]">{c.name.charAt(0)}</div>
            <div><h3 className="font-nunito font-extrabold tracking-tight text-2xl text-[#2D3748]">{c.name}</h3><p className="flex items-center gap-1 text-sm text-[#FF7E67]"><Coins className="h-4 w-4" />{c.points || 0} points</p></div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-[#718096]" /></button>
        </div>

        <Sec icon={Check} title="Chores">
          {d.chores.map((ch) => (
            <div key={ch.id} className="flex items-center justify-between rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2.5">
              <span className={`text-sm ${ch.approved ? "text-[#A0AEC0] line-through" : "text-[#2D3748]"}`}>{ch.title} <span className="text-[#FF7E67]">+{ch.points}</span></span>
              {ch.approved ? <Trophy className="h-4 w-4 text-[#FF7E67]" /> :
                ch.status === "done" ? <button onClick={() => act(() => api.post(`/family/chores/${ch.id}/approve`))} className="rounded-full bg-sunrise px-3 py-1 text-xs font-bold text-[#2D3748]">Approve</button> :
                <button onClick={() => act(() => api.post(`/family/chores/${ch.id}/complete`))} className="rounded-full border border-[#FFE8E3] px-3 py-1 text-xs text-[#718096]">Mark done</button>}
            </div>
          ))}
          <Adder ph="New chore" val={inputs.chore} onCh={(v) => si("chore", v)}
            onAdd={() => inputs.chore && act(() => api.post(`/family/children/${cid}/chores`, { title: inputs.chore, points: Number(inputs.chorePts) }).then(() => si("chore", "")))} />
        </Sec>

        <Sec icon={Gift} title="Rewards">
          {d.rewards.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2.5">
              <span className={`text-sm ${r.redeemed ? "text-[#A0AEC0] line-through" : "text-[#2D3748]"}`}>{r.title} <span className="text-[#FF7E67]">{r.cost} pts</span></span>
              {!r.redeemed && <button onClick={() => act(() => api.post(`/family/rewards/${r.id}/redeem`))} className="rounded-full bg-sunrise px-3 py-1 text-xs font-bold text-[#2D3748]">Redeem</button>}
            </div>
          ))}
          <Adder ph="New reward" val={inputs.reward} onCh={(v) => si("reward", v)}
            onAdd={() => inputs.reward && act(() => api.post(`/family/children/${cid}/rewards`, { title: inputs.reward, cost: Number(inputs.rewardCost) }).then(() => si("reward", "")))} />
        </Sec>

        <Sec icon={BookOpen} title="Reading">
          {d.reading.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2.5">
              <p className="text-sm text-[#2D3748]">{r.title}</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#FFE8E3]"><div className="h-full rounded-full bg-sunrise" style={{ width: `${Math.min(100, (r.pages / (r.target_pages || 1)) * 100)}%` }} /></div>
              <p className="mt-1 text-xs text-[#718096]">{r.pages}/{r.target_pages} pages</p>
            </div>
          ))}
          <Adder ph="Book title" val={inputs.reading} onCh={(v) => si("reading", v)}
            onAdd={() => inputs.reading && act(() => api.post(`/family/children/${cid}/reading`, { title: inputs.reading, target_pages: 100 }).then(() => si("reading", "")))} />
        </Sec>

        <Sec icon={Flame} title="Habits">
          {d.habits.map((h) => (
            <button key={h.id} onClick={() => act(() => api.post(`/family/habits/${h.id}/log`))} className="flex w-full items-center justify-between rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2.5 hover:border-[#FF7E67]">
              <span className="text-sm text-[#2D3748]">{h.name}</span><span className="flex items-center gap-1 text-xs text-[#FF7E67]"><Flame className="h-4 w-4" />{h.streak}</span>
            </button>
          ))}
          <Adder ph="New habit" val={inputs.habit} onCh={(v) => si("habit", v)}
            onAdd={() => inputs.habit && act(() => api.post(`/family/children/${cid}/habits`, { name: inputs.habit }).then(() => si("habit", "")))} />
        </Sec>

        <Sec icon={Coins} title="Allowance">
          {d.allowance.slice(0, 6).map((a) => (
            <div key={a.id} className="flex justify-between rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2 text-sm">
              <span className="text-[#4A5568]">{a.note || a.type}</span><span className={a.type === "spend" ? "text-[#E53E3E]" : "text-[#FF7E67]"}>{a.type === "spend" ? "-" : "+"}{a.amount}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <input value={inputs.allowance} onChange={(e) => si("allowance", e.target.value)} type="number" placeholder="Amount"
              className="h-10 flex-1 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
            <button onClick={() => inputs.allowance && act(() => api.post(`/family/children/${cid}/allowance`, { amount: Number(inputs.allowance), type: "earn", note: "Manual add" }).then(() => si("allowance", "")))}
              className="rounded-full bg-sunrise px-4 text-sm font-bold text-[#2D3748]">Add</button>
          </div>
        </Sec>
      </div>
    </div>
  );
}

const Sec = ({ icon: Icon, title, children }) => (
  <div className="mt-6">
    <p className="mb-2 flex items-center gap-2 font-nunito text-sm font-bold tracking-wide text-[#FF7E67]"><Icon className="h-4 w-4" /> {title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);
const Adder = ({ ph, val, onCh, onAdd }) => (
  <div className="flex gap-2">
    <input value={val} onChange={(e) => onCh(e.target.value)} placeholder={ph}
      className="h-10 flex-1 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
    <button onClick={onAdd} className="rounded-full bg-sunrise px-4 text-sm font-bold text-[#2D3748]"><Plus className="h-4 w-4" /></button>
  </div>
);
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4" onClick={onClose}>
    <div className="w-full max-w-md space-y-3 rounded-3xl border border-[#FFE8E3] bg-white p-7" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between"><h3 className="font-nunito text-xl font-bold tracking-tight text-[#2D3748]">{title}</h3><button onClick={onClose}><X className="h-5 w-5 text-[#718096]" /></button></div>
      {children}
    </div>
  </div>
);
